(function () {
  if (window.__mysticRouteHandoff) return;
  window.__mysticRouteHandoff = true;

  const doc = document;
  const root = doc.documentElement;
  const pageCache = new Map();
  const MAX_CACHE_SIZE = 10;
  let activeController = null;
  let isSwapping = false;
  let routeRequestId = 0;
  let routeStartedAt = 0;
  let routeFinishTimer = null;
  let inlineSearchCloseTimer = null;
  let inlineSearchPrefetchFrame = null;
  let inlineProductNavigationPending = false;
  let inlineSearchTouchStart = null;
  let inlineSearchSuppressClickUntil = 0;

  const SELECTOR = [
    'a[href]',
  ].join(',');

  const BLOCKED_PATHS = [
    '/account',
    '/authentication',
    '/cart',
    '/challenge',
    '/checkout',
    '/pages/wishlist',
    '/password',
  ];

  const trimCache = () => {
    while (pageCache.size > MAX_CACHE_SIZE) {
      pageCache.delete(pageCache.keys().next().value);
    }
  };

  const getSameOriginUrl = (link) => {
    if (!link) return null;

    let url;
    try {
      url = new URL(link.getAttribute('href'), window.location.href);
    } catch (_error) {
      return null;
    }

    return url.origin === window.location.origin ? url : null;
  };

  const isModifiedClick = (event) =>
    event.defaultPrevented ||
    (event.button != null && event.button !== 0) ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey;

  const getOpenDrawerMenu = () => {
    const drawer = doc.querySelector('#DrawerMenu');
    if (!drawer) return null;

    if (drawer.classList.contains('is-opened') || doc.querySelector('#DrawerMenuToggler.is-opened')) {
      return drawer;
    }

    return null;
  };

  const syncMobileMenuDrawerAnchor = () => {
    if (!window.matchMedia('(max-width: 1023px)').matches) {
      root.style.removeProperty('--mystic-mobile-drawer-anchor-height');
      return;
    }

    const headerSection = doc.querySelector('.shopify-section-header');
    const anchorHeight = headerSection?.getBoundingClientRect?.().bottom || 0;

    if (anchorHeight > 0) {
      root.style.setProperty('--mystic-mobile-drawer-anchor-height', `${Math.ceil(anchorHeight)}px`);
    }
  };

  const setMobileMenuDrawerClosingState = () => {
    window.clearTimeout(setMobileMenuDrawerClosingState.timer);

    if (!window.matchMedia('(max-width: 1023px)').matches) {
      doc.body.removeAttribute('data-mystic-mobile-menu-closing');
      doc.body.removeAttribute('data-mystic-mobile-menu-backdrop-closing');
      return;
    }

    syncMobileMenuDrawerAnchor();
    doc.body.setAttribute('data-mystic-mobile-menu-closing', 'true');

    setMobileMenuDrawerClosingState.timer = window.setTimeout(() => {
      doc.body.removeAttribute('data-mystic-mobile-menu-closing');
      doc.body.removeAttribute('data-mystic-mobile-menu-backdrop-closing');
    }, 650);

    doc.body.removeAttribute('data-mystic-mobile-menu-backdrop-closing');
  };

  const isSafeLink = (link, event) => {
    if (!link || isModifiedClick(event)) return false;
    if (link.target || link.hasAttribute('download')) return false;
    if (link.closest('[data-no-route-handoff], form')) return false;
    const href = link.getAttribute('href');
    if (!href || href === '#' || href.startsWith('#') || href.startsWith('javascript:') || href.startsWith('mailto:') || href.startsWith('tel:')) {
      return false;
    }

    return true;
  };

  const getRouteUrl = (link, event, options = {}) => {
    if (!isSafeLink(link, event)) return null;

    const url = getSameOriginUrl(link);
    if (!url) return null;

    if (!options.allowCurrent && url.href === window.location.href) return null;
    if (BLOCKED_PATHS.some((path) => url.pathname === path || url.pathname.startsWith(`${path}/`))) return null;
    if (url.pathname.startsWith('/products/')) return null;
    if (url.searchParams.has('section_id') || url.searchParams.has('view')) return null;
    if (/\.(?:json|js|css|png|jpe?g|gif|svg|webp|pdf|zip)$/i.test(url.pathname)) return null;

    return url;
  };

  const prefetchDocument = (url) => {
    if (!url || doc.querySelector(`link[rel="prefetch"][href="${CSS.escape(url.href)}"]`)) return;

    const link = doc.createElement('link');
    link.rel = 'prefetch';
    link.as = 'document';
    link.href = url.href;
    doc.head.appendChild(link);
  };

  const prefetchHeaderRoutes = () => {
    const eventStub = {
      altKey: false,
      button: 0,
      ctrlKey: false,
      defaultPrevented: false,
      metaKey: false,
      shiftKey: false,
    };

    doc.querySelectorAll('#DrawerMenu a[href], .shopify-section-header .mystic-recently-viewed-header-btn[href], .shopify-section-header .wl-header-btn[href*="/pages/wishlist"]').forEach((link) => {
      const url = getRouteUrl(link, eventStub, { allowCurrent: true });
      if (url) prefetchRoute(url);
    });
  };

  const fetchRoute = async (url, timeout = 4500) => {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url.href, {
        credentials: 'same-origin',
        signal: controller.signal,
        headers: {
          Accept: 'text/html',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) throw new Error(`Route fetch failed: ${response.status}`);

      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('text/html')) throw new Error('Route response was not HTML');

      const html = await response.text();
      const nextDocument = new DOMParser().parseFromString(html, 'text/html');
      const nextMain = nextDocument.querySelector('#MainContent');
      if (!nextMain) throw new Error('Route response did not include #MainContent');

      return { nextDocument, nextMain };
    } finally {
      window.clearTimeout(timeoutId);
    }
  };

  const prefetchRoute = (url) => {
    if (!url || pageCache.has(url.href)) return;

    const pending = fetchRoute(url, 6000)
      .then((page) => {
        pageCache.set(url.href, page);
        trimCache();
        return page;
      })
      .catch(() => {
        pageCache.delete(url.href);
        return null;
      });

    pageCache.set(url.href, pending);
  };

  const prefetchInlineSearchResults = () => {
    const searchContent = doc.querySelector('.shopify-section-header .search__content');
    if (!searchContent) return;

    const eventStub = {
      altKey: false,
      button: 0,
      ctrlKey: false,
      defaultPrevented: false,
      metaKey: false,
      shiftKey: false,
    };

    let prefetched = 0;
    searchContent.querySelectorAll('a[href]').forEach((link) => {
      if (prefetched >= 8) return;
      if (!link.closest('.predictive-search-results-grid, .predictive-search-discovery, .predictive-search-services-section')) return;

      const url = getSameOriginUrl(link);
      if (!url || !url.pathname.startsWith('/products/')) return;

      prefetched += 1;
      prefetchDocument(url);
    });
  };

  const resolveRoute = async (url) => {
    const cached = pageCache.get(url.href);
    if (cached) {
      const page = typeof cached.then === 'function' ? await cached : cached;
      if (page) {
        return {
          nextDocument: page.nextDocument,
          nextMain: page.nextMain.cloneNode(true),
        };
      }
    }

    const page = await fetchRoute(url, 4500);
    pageCache.set(url.href, page);
    trimCache();

    return {
      nextDocument: page.nextDocument,
      nextMain: page.nextMain.cloneNode(true),
    };
  };

  const replaceOrRemove = (selector, nextDocument) => {
    const current = doc.querySelector(selector);
    const next = nextDocument.querySelector(selector);

    if (current && next) {
      current.replaceWith(doc.importNode(next, true));
    } else if (current && !next) {
      current.remove();
    } else if (!current && next) {
      doc.head.appendChild(doc.importNode(next, true));
    }
  };

  const syncHead = (nextDocument) => {
    doc.title = nextDocument.title || doc.title;
    replaceOrRemove('meta[name="description"]', nextDocument);
    replaceOrRemove('link[rel="canonical"]', nextDocument);
  };

  const syncHeaderMenus = (nextDocument) => {
    const currentSecondary = doc.querySelector('.shopify-section-header .mystic-header-secondary-nav');
    const nextSecondary = nextDocument.querySelector('.shopify-section-header .mystic-header-secondary-nav');

    if (currentSecondary && nextSecondary) {
      currentSecondary.replaceWith(doc.importNode(nextSecondary, true));
    } else if (!currentSecondary && nextSecondary) {
      const headerDropdown = doc.querySelector('.shopify-section-header [data-header-dropdown-backdrop]');
      const header = headerDropdown?.parentElement || doc.querySelector('.shopify-section-header .header');
      const importedSecondary = doc.importNode(nextSecondary, true);

      if (headerDropdown) {
        headerDropdown.before(importedSecondary);
      } else {
        header?.appendChild(importedSecondary);
      }
    }

    const currentDrawer = doc.querySelector('#DrawerMenu');
    const nextDrawer = nextDocument.querySelector('#DrawerMenu');

    if (currentDrawer && nextDrawer) {
      currentDrawer.replaceWith(doc.importNode(nextDrawer, true));
    } else if (!currentDrawer && nextDrawer) {
      doc.body.prepend(doc.importNode(nextDrawer, true));
    }
  };

  const restartScripts = (container) => {
    container.querySelectorAll('script').forEach((script) => {
      const replacement = doc.createElement('script');
      Array.from(script.attributes).forEach((attribute) => {
        replacement.setAttribute(attribute.name, attribute.value);
      });
      replacement.textContent = script.textContent;
      script.replaceWith(replacement);
    });
  };

  const closeOpenDrawer = () => {
    const drawerMenu = getOpenDrawerMenu();
    if (!drawerMenu) return;

    if (typeof drawerMenu.close === 'function') {
      drawerMenu.close();
      return;
    }

    if (typeof drawerMenu.hide === 'function') {
      drawerMenu.hide();
      return;
    }

    drawerMenu.querySelector('.drawer-menu__close-button-wrapper button, .drawer-menu__close-button-wrapper')?.click();
  };

  const clearInlineSearchInstantClose = () => {
    if (inlineSearchCloseTimer) {
      window.clearTimeout(inlineSearchCloseTimer);
      inlineSearchCloseTimer = null;
    }

    root.classList.remove('mystic-inline-search-instant-close');
  };

  const cleanupRouteOverlays = () => {
    doc.body.classList.remove('predictive-search-active', 'predictive-search-overlay-closing', 'main-search-loading');
    doc.body.removeAttribute('data-mystic-mobile-theme-backdrop');
    doc.body.removeAttribute('data-mystic-mobile-theme-backdrop-closing');
    doc.body.removeAttribute('data-mystic-mobile-theme-backdrop-releasing');

    doc.querySelector('body-element.body-overlay')?.classList.remove('visible');
    doc.querySelector('body-element.body-overlay')?.removeAttribute('data-mystic-mobile-theme-backdrop');
    doc.querySelector('.mystic-mobile-drawer-backdrop')?.classList.remove('is-visible', 'is-closing');

    doc.querySelectorAll('.search__content.custom-drawer-open, .search__content.custom-drawer-closing').forEach((content) => {
      content.classList.remove('custom-drawer-open', 'custom-drawer-closing');
    });

    doc.querySelectorAll('[aria-expanded="true"][aria-controls*="search"], [aria-expanded="true"][aria-label*="Search"]').forEach((control) => {
      control.setAttribute('aria-expanded', 'false');
    });
  };

  const isInlineSearchLink = (link) =>
    !!link?.closest('.shopify-section-header .search__content');

  const isMobileInlineSearchGesture = (event) =>
    event.pointerType === 'touch' ||
    window.matchMedia?.('(pointer: coarse)')?.matches ||
    window.innerWidth < 1024;

  const closeInlineSearchForNavigation = () => {
    doc.body.classList.remove('predictive-search-active');
    doc.body.classList.add('predictive-search-overlay-closing');

    doc.querySelectorAll('.shopify-section-header .search__content.custom-drawer-open').forEach((content) => {
      content.classList.remove('custom-drawer-open');
      content.classList.add('custom-drawer-closing');
    });

    doc.querySelectorAll('[aria-expanded="true"][aria-controls*="search"], [aria-expanded="true"][aria-label*="Search"]').forEach((control) => {
      control.setAttribute('aria-expanded', 'false');
    });
  };

  const navigateInlineSearchProduct = (url) => {
    if (!url || inlineProductNavigationPending) return;

    inlineProductNavigationPending = true;
    startRouteUi();
    closeInlineSearchForNavigation();
    cleanupRouteOverlays();

    window.setTimeout(() => {
      nativeNavigate(url);
    }, 80);
  };

  const handleInlineSearchPointerDown = (event) => {
    if (!(event.target instanceof Element)) return;

    const link = event.target.closest(SELECTOR);
    const url = getRouteUrl(link, event);
    const inlineProductUrl = getSameOriginUrl(link);
    const isInlineProduct = isInlineSearchLink(link) && inlineProductUrl?.pathname.startsWith('/products/');
    if ((!url && !isInlineProduct) || !isInlineSearchLink(link)) return;

    if (isMobileInlineSearchGesture(event)) {
      inlineSearchTouchStart = {
        x: event.clientX,
        y: event.clientY,
        link,
        touchLike: true,
      };

      if (isInlineProduct) {
        prefetchDocument(inlineProductUrl);
      }

      return;
    }

    if (inlineSearchCloseTimer) window.clearTimeout(inlineSearchCloseTimer);

    root.classList.add('mystic-inline-search-instant-close');

    if (isInlineProduct) {
      prefetchDocument(inlineProductUrl);
      navigateInlineSearchProduct(inlineProductUrl);
    } else {
      cleanupRouteOverlays();
      routeTo(url, link);
    }

    inlineSearchCloseTimer = window.setTimeout(() => {
      root.classList.remove('mystic-inline-search-instant-close');
      inlineProductNavigationPending = false;
      inlineSearchCloseTimer = null;
    }, 1200);
  };

  const handleInlineSearchPointerMove = (event) => {
    if (!inlineSearchTouchStart || !inlineSearchTouchStart.touchLike) return;

    const deltaX = Math.abs(event.clientX - inlineSearchTouchStart.x);
    const deltaY = Math.abs(event.clientY - inlineSearchTouchStart.y);

    if (deltaY > 8 || deltaX > 12) {
      inlineSearchSuppressClickUntil = Date.now() + 500;
      inlineSearchTouchStart = null;
    }
  };

  const handleInlineSearchPointerEnd = (event) => {
    if (inlineSearchTouchStart?.touchLike || event.pointerType === 'touch') {
      inlineSearchTouchStart = null;
    }
  };

  const finishSwap = (page, url, shouldPushState) => {
    const currentMain = doc.querySelector('#MainContent');
    if (!currentMain) throw new Error('Current page does not include #MainContent');

    syncHead(page.nextDocument);
    doc.body.className = page.nextDocument.body.className;
    currentMain.replaceWith(doc.importNode(page.nextMain, true));
    syncHeaderMenus(page.nextDocument);

    const newMain = doc.querySelector('#MainContent');
    if (newMain) restartScripts(newMain);

    if (shouldPushState) {
      window.history.pushState({ mysticRouteHandoff: true }, '', url.href);
    }

    window.scrollTo(0, 0);

    if (window.lazySizes?.loader?.checkElems) {
      window.lazySizes.loader.checkElems();
    }

    clearInlineSearchInstantClose();
    cleanupRouteOverlays();

    doc.dispatchEvent(new CustomEvent('mystic:route-handoff:complete', { detail: { url: url.href } }));
    window.requestAnimationFrame(prefetchHeaderRoutes);
  };

  const nativeNavigate = (url) => {
    window.location.href = url.href;
  };

  const setRouteBeamOffset = () => {
    const header =
      doc.querySelector('.header') ||
      doc.querySelector('header') ||
      doc.querySelector('[data-section-type="header"]');
    const headerBottom = header?.getBoundingClientRect?.().bottom || 0;

    root.style.setProperty('--mystic-route-beam-top', `${Math.max(0, Math.round(headerBottom))}px`);
  };

  const getRouteBeam = () => {
    let beam = doc.querySelector('.mystic-route-beam');

    if (!beam) {
      beam = doc.createElement('div');
      beam.className = 'mystic-route-beam';
      beam.setAttribute('aria-hidden', 'true');
      doc.body.appendChild(beam);
    }

    return beam;
  };

  const startRouteUi = (options = {}) => {
    if (routeFinishTimer) {
      window.clearTimeout(routeFinishTimer);
      routeFinishTimer = null;
    }

    routeStartedAt = window.performance?.now?.() || Date.now();

    if (options.showBeam === false || options.suppressBeam || getOpenDrawerMenu()) {
      root.classList.remove('mystic-route-handoff-active');
      doc.querySelector('.mystic-route-beam')?.classList.remove('is-active');
      return;
    }

    setRouteBeamOffset();
    root.classList.add('mystic-route-handoff-active');
    getRouteBeam().classList.add('is-active');
  };

  const finishRouteUi = () => {
    const now = window.performance?.now?.() || Date.now();
    const elapsed = Math.max(0, now - routeStartedAt);
    const remaining = Math.max(0, 360 - elapsed);

    routeFinishTimer = window.setTimeout(() => {
      root.classList.remove('mystic-route-handoff-active');
      doc.querySelector('.mystic-route-beam')?.classList.remove('is-active');
      routeFinishTimer = null;
    }, remaining);
  };

  window.MysticRouteHandoff = window.MysticRouteHandoff || {};
  window.MysticRouteHandoff.start = startRouteUi;
  window.MysticRouteHandoff.finish = finishRouteUi;

  const routeTo = async (url, link, shouldPushState = true, options = {}) => {
    const requestId = (routeRequestId += 1);
    isSwapping = true;
    startRouteUi(options);

    if (activeController) activeController.abort();
    activeController = new AbortController();

    cleanupRouteOverlays();
    if (!options.keepDrawerOpen) closeOpenDrawer();

    try {
      const page = await resolveRoute(url);
      if (requestId !== routeRequestId) return;

      finishSwap(page, url, shouldPushState);
      finishRouteUi();
      isSwapping = false;
    } catch (error) {
      if (requestId !== routeRequestId) return;

      finishRouteUi();
      isSwapping = false;
      clearInlineSearchInstantClose();
      console.warn('[Mystic route handoff] Native navigation fallback.', error);
      nativeNavigate(url);
    }
  };

  window.MysticRouteHandoff.navigate = (href) => {
    const url = new URL(href, window.location.href);
    return routeTo(url, null, true);
  };

  window.MysticRouteHandoff.navigateBehindDrawer = (href) => {
    const url = new URL(href, window.location.href);
    return routeTo(url, null, true, { keepDrawerOpen: true, suppressBeam: true });
  };

  const handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const link = event.target.closest(SELECTOR);

    const inlineProductUrl = getSameOriginUrl(link);
    if (inlineProductUrl?.pathname.startsWith('/products/') && isInlineSearchLink(link) && !isModifiedClick(event)) {
      if (Date.now() < inlineSearchSuppressClickUntil) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      navigateInlineSearchProduct(inlineProductUrl);
      return;
    }

    const url = getRouteUrl(link, event);
    if (!url) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    routeTo(url, link);
  };

  const handlePrefetch = (event) => {
    if (!(event.target instanceof Element)) return;

    const link = event.target.closest(SELECTOR);
    const url = getRouteUrl(link, event);
    if (!url) return;
    prefetchRoute(url);
  };

  if ('MutationObserver' in window) {
    const drawerMenu = doc.querySelector('#DrawerMenu');
    if (drawerMenu) {
      let drawerWasOpen = drawerMenu.classList.contains('is-opened');

      new MutationObserver(() => {
        const drawerIsOpen = drawerMenu.classList.contains('is-opened');

        if (drawerIsOpen) {
          syncMobileMenuDrawerAnchor();
          doc.body.removeAttribute('data-mystic-mobile-menu-closing');
          doc.body.removeAttribute('data-mystic-mobile-menu-backdrop-closing');
          window.clearTimeout(setMobileMenuDrawerClosingState.timer);
        }

        if (drawerWasOpen && !drawerIsOpen) {
          setMobileMenuDrawerClosingState();
        }

        drawerWasOpen = drawerIsOpen;
      }).observe(drawerMenu, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }
  }

  doc.addEventListener('click', handleClick, true);
  doc.addEventListener('pointerdown', handleInlineSearchPointerDown, { capture: true, passive: true });
  doc.addEventListener('pointermove', handleInlineSearchPointerMove, { capture: true, passive: true });
  doc.addEventListener('pointerup', handleInlineSearchPointerEnd, { capture: true, passive: true });
  doc.addEventListener('pointercancel', handleInlineSearchPointerEnd, { capture: true, passive: true });
  doc.addEventListener('pointerover', handlePrefetch, { capture: true, passive: true });
  doc.addEventListener('pointerenter', handlePrefetch, { capture: true, passive: true });
  doc.addEventListener('touchstart', handlePrefetch, { capture: true, passive: true });
  doc.addEventListener('focusin', handlePrefetch, true);

  const inlineSearchObserver = new MutationObserver(() => {
    if (inlineSearchPrefetchFrame) return;

    inlineSearchPrefetchFrame = window.requestAnimationFrame(() => {
      inlineSearchPrefetchFrame = null;
      prefetchInlineSearchResults();
    });
  });

  inlineSearchObserver.observe(doc.body, {
    childList: true,
    subtree: true,
  });

  window.addEventListener('popstate', () => {
    routeTo(new URL(window.location.href), null, false);
  });

  window.setTimeout(prefetchHeaderRoutes, 250);
})();
